import React, { useEffect } from 'react';
import {
  getVideoSettingsWithProGalleryValues,
  PG_LAYOUT_INFO_SIZE_TYPE_OPTIONS,
  type LayoutName,
  type LayoutSettingsType,
  type PgLayoutImageCropTypeOptions,
  type POST_LIST_WIDGET_DEFAULTS,
  type PostAction,
  type Section,
  type VideoSection,
  type LayoutType,
  type Layout,
} from '@wix/communities-blog-client-common';
import {
  LAYOUT_IMAGE_RATIO_MAP,
  TEXT_BOX_ALIGNMENT_MAP,
} from '../../constants/pro-gallery-options';
import useLayoutProps from '../../hooks/use-layout-props';
import {
  getFeedBorderColor,
  getIsCreatedWithResponsiveEditor,
  getLayoutCardsRowType,
  getLayoutContentHeight,
  getLayoutContentHeightType,
  getLayoutImageAlignment,
  getLayoutImageCropType,
  getLayoutImageProportions,
  getLayoutImageRatio,
  getLayoutImageResizingMode,
  getLayoutImageWidth,
  getLayoutMargins,
  getLayoutPostSize,
  getLayoutPostsPerRow,
  getLayoutSidesPadding,
  getLayoutSpacing,
  getUseMobileLayoutSettings,
} from '../../selectors/app-settings-selectors';
import { getProGalleryViewMode } from '../../selectors/progallery-view-mode-selectors';
import { isUsingResponsiveStyling } from '../../services/post-list-pro-gallery';
import { getIsMobile } from '../../store/basic-params/basic-params-selectors';
import { type NormalizedPost } from '../../types';
import LoadMore, { type LoadMoreProps } from '../load-more/load-more';
import Loader from '../loader';
import { useResponsiveContext } from '../responsive-listener/responsive-context';
import { useSelector } from '../runtime-context';
import calculateContentHeight from './calculate-content-height';
import { type ProGalleryOptions } from './gallery-options';
import { PostListProGalleryComponent } from './post-list-pro-gallery-component';

export type LayoutDefaults = Partial<typeof POST_LIST_WIDGET_DEFAULTS>;

export type PickedLayoutOptions = Pick<
  ProGalleryOptions,
  | 'showArrows'
  | 'arrowsSize'
  | 'autoSlide'
  | 'pauseTime'
  | 'arrowsPosition'
  | 'arrowsColor'
  | 'loop'
>;

type BaseProps = {
  allPosts: NormalizedPost[];
  canSeeMoreButton: (post: NormalizedPost) => boolean;
  className?: string;
  currentPagePosts: NormalizedPost[];
  domId?: string;
  hideCoverImage?: boolean;
  isLoading: boolean;
  layoutDefaults?: LayoutDefaults;
  layoutOptions?: PickedLayoutOptions;
  /** Unsafe to use by itself. Safe version is returned by useSlice() */
  layoutType?: Layout | LayoutType;
  /** Unsafe to use by itself. Safe version is returned by useSlice() */
  layoutName?: LayoutName;
  /** Unsafe to use by itself. Safe version is returned by useSlice() */
  section: Section;
  pageStart?: number;
  showCreatePostAction: boolean;
  visibleActions?: PostAction[];
};

type Props =
  | BaseProps
  | (BaseProps & {
      entityCount: number;
      loadMore?: LoadMoreProps['loadMore'];
    });

const PostListProGallery: React.FC<Props> = (props) => {
  const slice = useSlice({
    section: props.section,
    layoutType: props.layoutType,
    layoutName: props.layoutName,
    layoutDefaults: props.layoutDefaults,
  });

  useEffect(() => {
    if (slice.createdWithResponsiveEditor) {
      document.documentElement.classList.add('enable-scroll');
    }
  }, [slice.createdWithResponsiveEditor]);

  return 'loadMore' in props && props.loadMore ? (
    <LoadMore
      loadMore={props.loadMore}
      loader={<Loader />}
      isLoading={props.isLoading}
      hasMore={props.allPosts.length < props.entityCount}
      pageStart={props.pageStart}
    >
      <PostListProGalleryComponent
        allPosts={props.allPosts}
        layoutName={props.layoutName}
        section={props.section}
        layoutType={props.layoutType}
        layoutDefaults={props.layoutDefaults}
        layoutOptions={props.layoutOptions}
        canSeeMoreButton={props.canSeeMoreButton}
        className={props.className}
        domId={props.domId}
        hideCoverImage={props.hideCoverImage}
        visibleActions={props.visibleActions}
      />
    </LoadMore>
  ) : (
    <PostListProGalleryComponent
      allPosts={props.allPosts}
      layoutName={props.layoutName}
      section={props.section}
      layoutType={props.layoutType}
      layoutDefaults={props.layoutDefaults}
      layoutOptions={props.layoutOptions}
      canSeeMoreButton={props.canSeeMoreButton}
      className={props.className}
      domId={props.domId}
      hideCoverImage={props.hideCoverImage}
      visibleActions={props.visibleActions}
    />
  );
};

export function useSlice({
  section,
  layoutDefaults = {},
  layoutName,
  layoutType,
}: Pick<Props, 'section' | 'layoutType' | 'layoutName' | 'layoutDefaults'>) {
  const { rootWidth } = useResponsiveContext();
  const layoutProps = useLayoutProps({ layoutType, section });

  const hostWidth = useSelector((_, host) => host.dimensions?.width as number);
  const createdWithResponsiveEditor = useSelector(getIsCreatedWithResponsiveEditor);
  const usingResponsiveStyling = isUsingResponsiveStyling({
    createdWithResponsiveEditor,
    section: layoutProps.section!,
  });

  const layoutMarginsFromSettings = useSelector((state) =>
    getLayoutMargins({
      state,
      section: layoutProps.section,
      layoutType: layoutProps.layoutType,
    }),
  );

  const layoutMargins = usingResponsiveStyling ? 0 : layoutMarginsFromSettings;

  const layoutAutoHeightEnabled = useSelector(
    (state) =>
      getLayoutContentHeightType({
        state,
        section: layoutProps.section,
        layoutType: layoutProps.layoutType,
      }) === PG_LAYOUT_INFO_SIZE_TYPE_OPTIONS.AUTO,
  );
  const unsafeBorderColor = useSelector((state) =>
    getFeedBorderColor({ state, section: layoutProps.section! }),
  );

  const isMobile = useSelector(getIsMobile);
  const layoutContentHeight =
    useSelector((state) =>
      layoutAutoHeightEnabled
        ? calculateContentHeight(state, layoutProps.section!, layoutProps.layoutType)
        : getLayoutContentHeight({
            state,
            section: layoutProps.section,
            layoutType: layoutProps.layoutType,
          }),
    ) ?? layoutDefaults.contentHeight!;

  const textBoxAlignment = useSelector(
    (state) =>
      TEXT_BOX_ALIGNMENT_MAP[
        getLayoutImageAlignment({
          state,
          section: layoutProps.section,
          layoutType: layoutProps.layoutType,
        })!
      ],
  );
  const layoutImageProportions = useSelector(
    (state) =>
      getLayoutImageProportions({
        state,
        section: layoutProps.section,
        layoutType: layoutProps.layoutType,
      })!,
  );
  const layoutImageWidth = useSelector(
    (state) =>
      getLayoutImageWidth({
        state,
        section: layoutProps.section,
        layoutType: layoutProps.layoutType,
      })!,
  );
  const layoutImageResizingMode = useSelector((state) =>
    getLayoutImageResizingMode({
      state,
      section: layoutProps.section,
      layoutType: layoutProps.layoutType,
    }),
  );
  const layoutImageRatio = useSelector(
    (state) =>
      LAYOUT_IMAGE_RATIO_MAP[
        getLayoutImageRatio({
          state,
          section: layoutProps.section,
          layoutType: layoutProps.layoutType,
        }) ?? layoutDefaults.imageRatio!
      ],
  );
  const layoutImageCropType = useSelector(
    (state) =>
      getLayoutImageCropType({
        state,
        section: layoutProps.section,
        layoutType: layoutProps.layoutType,
      }) as unknown as PgLayoutImageCropTypeOptions,
  );
  const layoutPostSize = useSelector((state) =>
    getLayoutPostSize({
      state,
      hostWidth,
      rootWidth,
      layoutMargins,
      section: layoutProps.section!,
      layoutType: layoutProps.layoutType as LayoutSettingsType,
    }),
  );
  const layoutCardsRowType = useSelector(
    (state) =>
      getLayoutCardsRowType({
        state,
        section: layoutProps.section,
        layoutType: layoutProps.layoutType,
      }) ?? layoutDefaults.layoutCardsRowType!,
  );
  const layoutPostsPerRow = useSelector(
    (state) =>
      getLayoutPostsPerRow({
        state,
        section: layoutProps.section,
        layoutType: layoutProps.layoutType,
      }) ?? layoutDefaults.layoutPostsPerRow!,
  );
  const layoutSidesPadding = useSelector(
    (state) =>
      getLayoutSidesPadding({
        state,
        section: layoutProps.section,
        layoutType: layoutProps.layoutType,
      }) ?? layoutDefaults.sidesPadding!,
  );
  const layoutSpacing = useSelector(
    (state) =>
      getLayoutSpacing({
        state,
        section: layoutProps.section,
        layoutType: layoutProps.layoutType,
      }) ?? layoutDefaults.spacing!,
  );
  const viewMode = useSelector((state) => getProGalleryViewMode(state, rootWidth));
  const videoSettings = useSelector((state) =>
    getVideoSettingsWithProGalleryValues(state, layoutProps.section as VideoSection),
  );
  const useMobilePostListLayout = useSelector(getUseMobileLayoutSettings);

  return {
    hostWidth,
    section: layoutProps.section,
    layoutType: layoutProps.layoutType,
    layoutName: layoutName ?? layoutProps.layoutName,
    createdWithResponsiveEditor,
    layoutAutoHeightEnabled,
    rootWidth,
    isMobile,
    borderColor:
      typeof unsafeBorderColor === 'string'
        ? unsafeBorderColor
        : unsafeBorderColor.value || unsafeBorderColor.themeName!,
    layoutContentHeight,
    textBoxAlignment,
    layoutImageProportions,
    layoutImageWidth,
    layoutImageResizingMode,
    layoutImageRatio,
    layoutImageCropType,
    layoutMargins,
    layoutPostSize,
    layoutCardsRowType,
    layoutPostsPerRow,
    layoutSidesPadding,
    layoutSpacing,
    viewMode,
    videoSettings,
    useMobilePostListLayout,
  };
}

export default PostListProGallery;
